import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { FixedPositionedDropdown } from "src/base-components/FixedPositionedDropDown";
import { Pill } from "src/base-components/Pill";
import {
  FeatureMappingOutput,
  FeatureQueryStatus,
  PreviewQueryRequestEnvironmentEnum,
  SetFeatureQueryStatusRequestEnvironmentEnum,
} from "src/clients/features-control/api";
import { DropdownItem } from "src/design-system/Dropdown";
import { useModal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { QueryToggleConfirmationModal } from "src/featureCatalogue/Queries/QueryToggleConfirmationModal";
import { useSetFeatureQueryStatus } from "src/featureCatalogue/queries";
import { useCapabilities } from "src/hooks/useCapabilities";
import { errorMessage } from "src/utils/stringUtils";

type StatusDropdownProps = {
  value: FeatureQueryStatus;
  disabled?: boolean;
  environment: PreviewQueryRequestEnvironmentEnum;
  queryId: string;
  etag: string;
  entities: string[];
  event: string;
  features: FeatureMappingOutput[];
};

type Status = {
  key: FeatureQueryStatus;
  value: string;
};

const STATUSES: Status[] = [
  {
    key: FeatureQueryStatus.ACTIVE,
    value: "Active",
  },
  {
    key: FeatureQueryStatus.INACTIVE,
    value: "Inactive",
  },
];

export const StatusDropdown: React.FC<StatusDropdownProps> = ({
  value,
  disabled = false,
  environment,
  queryId,
  etag,
  entities,
  event,
  features,
}) => {
  const { openModal: openConfirmationModal, isOpen, closeModal } = useModal();
  const { mutateAsync: setFeatureQueryStatus } = useSetFeatureQueryStatus();
  const { featureQueriesCatalogue } = useCapabilities();
  const isActive = value === FeatureQueryStatus.ACTIVE;
  const isLive = environment === PreviewQueryRequestEnvironmentEnum.LIVE;
  const canChangeStatus = isLive
    ? featureQueriesCatalogue.canChangeLiveStatus
    : featureQueriesCatalogue.canChangeSandboxStatus;

  const setQueryStatus = async () => {
    try {
      await setFeatureQueryStatus({
        id: queryId,
        environment:
          environment === PreviewQueryRequestEnvironmentEnum.SANDBOX
            ? SetFeatureQueryStatusRequestEnvironmentEnum.SANDBOX
            : SetFeatureQueryStatusRequestEnvironmentEnum.LIVE,
        status: isActive
          ? FeatureQueryStatus.INACTIVE
          : FeatureQueryStatus.ACTIVE,
        etag,
      });

      toastActions.success({
        title: isActive ? "Query Deactivated" : "Query Activated",
        description: isActive
          ? "Query is successfully deactivated."
          : "Query is successfully activated.",
      });
    } catch (error) {
      const message = errorMessage(error);
      toastActions.failure({
        title: isActive ? "Deactivation Failed" : "Activation Failed",
        description: isActive
          ? message || "Query couldn't be deactivated. Try again."
          : message || "Query couldn't be activated. Try again.",
      });
    }
  };

  const handleConfirmation = () => {
    closeModal();
    setTimeout(setQueryStatus, 100);
  };

  return (
    <>
      <FixedPositionedDropdown<string, FeatureQueryStatus>
        buttonClassName="border-0 ring-0 focus:ring-0 "
        disabled={disabled || !canChangeStatus}
        elements={STATUSES.map((s) => ({
          ...s,
          disabled: s.key === value,
        }))}
        placement="bottom start"
        renderButtonValue={() => {
          const status = STATUSES.find((s) => s.key === value);
          return (
            <Tooltip
              body="You don't have permission to edit the status"
              disabled={canChangeStatus}
              placement="top"
              asChild
            >
              <div>
                <StatusBadge status={value} value={status?.value ?? ""} />
              </div>
            </Tooltip>
          );
        }}
        renderValue={({ value: displayValue, selected }) => (
          <DropdownItem
            data-loc={`status-${displayValue.toLowerCase()}`}
            data-selected={selected || undefined}
          >
            <span>{displayValue}</span>
          </DropdownItem>
        )}
        selected={value}
        onSelect={openConfirmationModal}
      />
      <QueryToggleConfirmationModal
        closeModal={closeModal}
        entities={entities}
        environment={environment}
        event={event}
        features={features}
        isActive={isActive}
        isOpen={isOpen}
        onConfirm={handleConfirmation}
      />
    </>
  );
};

const StatusBadge: React.FC<{
  status: FeatureQueryStatus;
  value: string;
}> = ({ status, value }) => {
  const isActive = status === FeatureQueryStatus.ACTIVE;

  return (
    <Pill size="sm" variant={isActive ? "green" : "gray"}>
      <Pill.Text>{value}</Pill.Text>
      <Pill.Icon
        color={isActive ? "text-green-500" : "text-gray-500"}
        icon={faChevronDown}
        size="xs"
      />
    </Pill>
  );
};
